import React from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippyjs/react';
import { formatHeaderDate, initials } from '../utils/offerHeaderHelpers';
import '../styles/lineup.css';

const ROLE_LABEL = {
  headline: 'Headliner',
  support: 'Support',
};

const STATUS_LABEL = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Confirmed',
  declined: 'Declined',
};

const STATUS_CLASS = {
  draft: 'lineup-status lineup-status--draft',
  sent: 'lineup-status lineup-status--sent',
  accepted: 'lineup-status lineup-status--confirmed',
  declined: 'lineup-status lineup-status--declined',
};

// Returns the agent-line string, or `null` when no agent is on file. The
// header omits the entire line in the null case (avoids a noisy "No agent
// on file" placeholder per design).
function agentLine(offer) {
  if (!offer.agent_name) return null;
  let line = `Agent: ${offer.agent_name}`;
  if (offer.last_sent_to) line += ' · Sent';
  return line;
}

// Builds the `Sat, Aug 23 · Bowery Ballroom` chip-line. Prefers the linked
// confirm fields, falls back to whatever the offer carries directly.
// Returns null when neither date nor venue is available.
function buildDateVenueLine(offer, confirm) {
  const dateStr = formatHeaderDate(confirm?.start_time || offer?.start_time);
  const venueName = confirm?.venue?.name || offer?.venue?.name || null;
  if (!dateStr && !venueName) return null;
  return [dateStr, venueName].filter(Boolean).join(' · ');
}

const V3OfferDealTermsHeader = ({
  offer,
  confirm,
  onPreview,
  onCopy,
  onCopyHolds,
  canCopyHolds,
  onDownload,
  onCreateHold,
  onCreateConfirm,
  canConfirm,
  onDelete,
  onSend,
  onAddContact,
  onEditPerformer,
  switchEditorUrl,
  showActions,
}) => {
  const role = ROLE_LABEL[offer.act_type] || 'Undefined';
  const status = STATUS_LABEL[offer.state] || offer.state || '';
  const statusClass = STATUS_CLASS[offer.state] || 'lineup-status';
  const sendLabel = offer.state === 'sent' ? 'Resend' : 'Send offer';
  const showSend = offer.state === 'draft' || offer.state === 'sent';
  const dateVenueLine = buildDateVenueLine(offer, confirm);

  const iconBtnStyle = {
    borderRadius: '8px',
    width: '36px',
    height: '36px',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--od-c-ffffff, white)',
    border: '1px solid var(--od-c-d5d7da, #d5d7da)',
    boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
    color: 'var(--od-c-414651, #414651)',
    cursor: 'pointer',
  };

  const dangerIconBtnStyle = {
    ...iconBtnStyle,
    border: '1px solid #fda29b',
    color: '#d92d20',
  };

  const disabledIconBtnStyle = {
    ...iconBtnStyle,
    opacity: 0.5,
    cursor: 'not-allowed',
  };

  // Matches the "+ New Event" primary button on the dashboard / calendar
  // (see CalendarViewV2.jsx:1962). Inline because the multi-layer inset
  // boxShadow is not part of Bootstrap's default btn-primary.
  const primaryBtnStyle = {
    fontWeight: 600,
    borderRadius: '8px',
    borderWidth: '1px',
    paddingTop: '5.6px',
    paddingBottom: '5.6px',
    fontSize: '14px',
    lineHeight: '21px',
    boxShadow:
      '0px 0px 0px 1px rgba(10, 13, 18, 0.18) inset, '
      + '0px -2px 0px 0px rgba(10, 13, 18, 0.05) inset, '
      + '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  };

  // The action toolbar is identical whether or not an artist is assigned: every
  // action operates on the offer/budget/holds, not the performer — preview,
  // copy, download, delete and create-hold all handle a null artist, and
  // create-confirm / send gate on offer state and recipients, not the artist. So
  // it renders in the artist-less empty state too (O-4167); that state previously
  // dropped the whole toolbar as a side effect of being one big add-artist tap
  // target. stopPropagation keeps a toolbar click/keypress in the empty state
  // from also firing the header's add-artist handler (a no-op in the normal
  // header, which is not itself clickable).
  const actionsToolbar = showActions && (
    <div
      className="deal-terms-header__actions"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {switchEditorUrl && (
        <Tippy content="Switch to simple editor" placement="top">
          <a
            href={switchEditorUrl}
            aria-label="Switch to simple editor"
            className="btn btn-sm"
            style={iconBtnStyle}
            data-testid="deal-terms-header-switch-to-simple"
          >
            <i className="far fa-table-layout" />
          </a>
        </Tippy>
      )}

      <Tippy content="Preview" placement="top">
        <button
          type="button"
          aria-label="Preview"
          onClick={onPreview}
          className="btn btn-sm"
          style={iconBtnStyle}
        >
          <i className="far fa-eye" />
        </button>
      </Tippy>

      <Tippy content="Copy offer" placement="top">
        <button
          type="button"
          aria-label="Copy"
          onClick={onCopy}
          className="btn btn-sm"
          style={iconBtnStyle}
        >
          <i className="far fa-copy" />
        </button>
      </Tippy>

      <Tippy content={canCopyHolds ? 'Copy hold dates' : 'No holds to copy'} placement="top">
        <span style={{ display: 'inline-flex' }}>
          <button
            type="button"
            aria-label="Copy hold dates"
            onClick={onCopyHolds}
            disabled={!canCopyHolds}
            className="btn btn-sm"
            style={canCopyHolds ? iconBtnStyle : disabledIconBtnStyle}
            data-testid="deal-terms-header-copy-holds"
          >
            <i className="far fa-clipboard" />
          </button>
        </span>
      </Tippy>

      <Tippy content="Download PDF" placement="top">
        <button
          type="button"
          aria-label="Download"
          onClick={onDownload}
          className="btn btn-sm"
          style={iconBtnStyle}
        >
          <i className="far fa-arrow-to-bottom" />
        </button>
      </Tippy>

      {onCreateHold && (
        <Tippy content="Create hold" placement="top">
          <button
            type="button"
            aria-label="Create hold"
            onClick={onCreateHold}
            className="btn btn-sm"
            style={iconBtnStyle}
            data-testid="deal-terms-header-create-hold"
          >
            <i className="far fa-calendar-plus" />
          </button>
        </Tippy>
      )}

      {onCreateConfirm && canConfirm && (
        <Tippy content="Create a Confirm" placement="top">
          <button
            type="button"
            aria-label="Create a Confirm"
            onClick={onCreateConfirm}
            className="btn btn-sm"
            style={iconBtnStyle}
            data-testid="deal-terms-header-create-confirm"
          >
            <i className="far fa-calendar-check" />
          </button>
        </Tippy>
      )}

      <Tippy content="Delete offer" placement="top">
        <button
          type="button"
          aria-label="Delete"
          onClick={onDelete}
          className="btn btn-sm"
          style={dangerIconBtnStyle}
        >
          <i className="far fa-trash" />
        </button>
      </Tippy>

      {showSend && (
        <button
          type="button"
          className="btn btn-primary small text-nowrap"
          style={primaryBtnStyle}
          onClick={onSend}
        >
          <i className="far fa-paper-plane mr-1" />
          {sendLabel}
        </button>
      )}
    </div>
  );

  // Empty-state variant — when the active offer has no primary performer and
  // the parent surface has wired an `onEditPerformer` handler, the entire
  // header becomes one tap target that opens the artist picker. Both desktop
  // (V3FinancePage) and mobile (V3FinancePageMobile) wire the handler, so this
  // renders on either surface for an offer with no primary performer yet — for
  // example one where a support act was added before the headliner. (A brand
  // new offer is created via the shared add-artist picker modal.)
  const isEmpty = !offer.primary_performer && !!onEditPerformer;
  if (isEmpty) {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onEditPerformer();
      }
    };
    return (
      <header
        className="deal-terms-header deal-terms-header--empty"
        role="button"
        tabIndex={0}
        aria-label="Add artist"
        onClick={onEditPerformer}
        onKeyDown={handleKeyDown}
        data-testid="deal-terms-header-empty"
      >
        <div className="deal-terms-header__avatar deal-terms-header__avatar--empty" aria-hidden="true">
          <i className="far fa-plus" />
        </div>
        <div className="deal-terms-header__body">
          <span className="deal-terms-header__eyebrow">
            <span>ARTIST OFFER</span>
            <span className="deal-terms-header__eyebrow-sep" aria-hidden="true">·</span>
            <span className={`deal-terms-header__status deal-terms-header__status--${offer.state || 'draft'}`}>
              <span className="deal-terms-header__status-dot" aria-hidden="true" />
              <span>{status || 'DRAFT'}</span>
            </span>
          </span>
          <button
            type="button"
            className="deal-terms-header__add-artist"
            tabIndex={-1}
          >
            <i className="far fa-plus" aria-hidden="true" />
            Add artist
          </button>
          <div className="deal-terms-header__add-hint">Add the performer this offer is for.</div>
        </div>
        {/* Keep every action that works without an artist. Only the artist-only
          * "change artist" pencil is absent here — adding the artist is what the
          * rest of the empty header already does. (O-4167) */}
        {actionsToolbar}
      </header>
    );
  }

  return (
    <header className="deal-terms-header">
      <div className="deal-terms-header__avatar">
        {offer.performer_avatar_url
          ? <img src={offer.performer_avatar_url} alt="" />
          : <span className="deal-terms-header__avatar-initials">{initials(offer.performer_name)}</span>
        }
      </div>

      <div className="deal-terms-header__body">
        <span className="deal-terms-header__eyebrow">
          <span>ARTIST OFFER</span>
          {/* Eyebrow status is mobile-first per the prototype; CSS hides it
            * on desktop and hides the name-row pill on mobile so the two
            * surfaces don't double up. */}
          <span className="deal-terms-header__eyebrow-sep" aria-hidden="true">·</span>
          <span className={`deal-terms-header__status deal-terms-header__status--${offer.state || 'draft'}`}>
            <span className="deal-terms-header__status-dot" aria-hidden="true" />
            <span>{status ? status.toUpperCase() : 'DRAFT'}</span>
          </span>
        </span>
        <div className="deal-terms-header__name-row">
          <h2 className="deal-terms-header__name">{offer.performer_name || 'Unnamed artist'}</h2>
          <span className="deal-terms-role">{role}</span>
          <span className={statusClass}>{status}</span>
          {/* Desktop entry point for swapping the offer's primary performer —
            * a pencil next to the artist name. Gated on onEditPerformer so
            * panes that don't wire it keep a read-only name. Hidden on mobile
            * via mobile.css — the mobile action sheet owns this there. */}
          {onEditPerformer && (
            <button
              type="button"
              className="deal-terms-header__change-artist"
              onClick={onEditPerformer}
              aria-label="Change artist"
              title="Change artist"
              data-testid="deal-terms-header-change-artist"
            >
              <i className="far fa-pen" aria-hidden="true" />
            </button>
          )}
        </div>
        {dateVenueLine && (
          <div
            className="deal-terms-header__date-venue"
            data-testid="deal-terms-header-date-venue"
          >
            {dateVenueLine}
          </div>
        )}
        {agentLine(offer) && (
          <div className="deal-terms-header__agent">{agentLine(offer)}</div>
        )}
        {onAddContact && (
          <button
            type="button"
            className="deal-terms-header__add-contact"
            onClick={onAddContact}
            data-testid="deal-terms-header-add-contact"
          >
            + Add contacts
          </button>
        )}
      </div>

      {actionsToolbar}
    </header>
  );
};

V3OfferDealTermsHeader.propTypes = {
  offer: PropTypes.shape({
    id: PropTypes.number,
    state: PropTypes.string,
    act_type: PropTypes.string,
    performer_name: PropTypes.string,
    performer_avatar_url: PropTypes.string,
    agent_name: PropTypes.string,
    last_sent_to: PropTypes.string,
    last_sent_at: PropTypes.string,
    start_time: PropTypes.string,
    venue: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
  confirm: PropTypes.shape({
    start_time: PropTypes.string,
    venue: PropTypes.shape({ name: PropTypes.string }),
  }),
  onPreview: PropTypes.func,
  onCopy: PropTypes.func,
  onCopyHolds: PropTypes.func,
  canCopyHolds: PropTypes.bool,
  onDownload: PropTypes.func,
  onCreateHold: PropTypes.func,
  onCreateConfirm: PropTypes.func,
  canConfirm: PropTypes.bool,
  onDelete: PropTypes.func,
  onSend: PropTypes.func,
  onAddContact: PropTypes.func,
  onEditPerformer: PropTypes.func,
  showActions: PropTypes.bool,
};

V3OfferDealTermsHeader.defaultProps = {
  confirm: null,
  onCopyHolds: null,
  canCopyHolds: false,
  onCreateHold: null,
  onCreateConfirm: null,
  canConfirm: false,
  onAddContact: null,
  onEditPerformer: null,
  showActions: true,
};

export default V3OfferDealTermsHeader;
